import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { BlackboardStreamService } from "../../../../services/blackboard-stream.service";
import { DebugModeService } from "../../../../services/debug-mode.service";
import { EvidenceProgressStore } from "../../../../services/evidence-progress-store.service";
import type { BlockStatus } from "../../../../model/block.types";
import type { ActionState } from "../../../../model/engine-run-state.types";
import type { FeedbackQuestion, PendingDecision, PlanData } from "../../../../model/plan.types";
import { nonDoneTaskIds } from "../../../../model/plan-state.utils";
import { PlanStepListComponent } from "../plan-shared/plan-step-list/plan-step-list.component";
import type { PlanStepListStep } from "../plan-shared/plan-step-list/plan-step-list.component";
import { frontendToolTaskIds } from "../plan-shared/plan-task-status";
import type { PlanTaskState } from "../plan-shared/plan-task-status";
import { HitlService } from "../../../../services/hitl.service";
import { PlanDebugDialogComponent } from "../plan-debug-dialog/plan-debug-dialog.component";

/**
 * Icon-only lifecycle state copy for the card header (V2 ruling): the state renders as ONE
 * icon tinted by semantics and the human state word lives in the tooltip only.
 */
interface PlanStateIcon {
  icon: string;
  stateClass: string;
  /** Human state word — the header tooltip copy. */
  word: string;
}

/** Static readable titles for the decision types the frontend catalog knows (doc 32 §3.4). Any
 *  other `decision_type` (e.g. `cost_approval`) renders verbatim — the fallback keeps unknown
 *  types honest instead of guessing labels beyond what the docs show. */
const DECISION_TYPE_TITLES: Readonly<Record<string, string>> = {
  plan_approval: "Plan approval",
  dry_run: "Dry run",
};

/**
 * Header icon per decision type (redesign): `plan_approval` → `rule`, `dry_run` → `science`,
 * anything else → the neutral `check_circle`. Display-only mapping; unknown types keep the
 * neutral icon so the row never renders icon-less.
 */
const DECISION_TYPE_ICONS: Readonly<Record<string, string>> = {
  plan_approval: "rule",
  dry_run: "science",
};

/** Readable title of one pending decision (W2): catalog title or the raw `decision_type`. */
function decisionTitle(decisionType: string): string {
  return DECISION_TYPE_TITLES[decisionType] ?? decisionType;
}

/** Header icon of one pending decision: catalog icon or the neutral `check_circle` fallback. */
function decisionIcon(decisionType: string): string {
  return DECISION_TYPE_ICONS[decisionType] ?? "check_circle";
}

/** Defensive boolean payload read (W2): `payload` is an open dict (doc 31 §5.3) — property
 *  access is guarded and type-checked, never trusted. */
function payloadBoolean(payload: Record<string, unknown>, key: string): boolean | null {
  const value = payload[key];
  return typeof value === "boolean" ? value : null;
}

/** Defensive finite-number payload read (W2) — same guard discipline as `payloadBoolean`. */
function payloadNumber(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * One interpreted row of the user-surface approval checklist (redesign): readable title,
 * header icon, and the payload readouts keyed by presence — "Volume: N" when the payload
 * carries a numeric `volume`, "Sample size: N" when it carries a numeric `sample_size`, and
 * the "Volume unknown" warning badge when `volume_known === false` (W2 defensive reads).
 */
interface PendingDecisionRow {
  decision: PendingDecision;
  title: string;
  /** Header icon per decision type (plan_approval → rule, dry_run → science, else check_circle). */
  icon: string;
  /** Numeric `volume` of the payload; null when absent (no volume row). */
  volume: number | null;
  /** Numeric `sample_size` of the payload; null when absent (no sample-size row). */
  sampleSize: number | null;
  /** True when `volume_known === false` — renders the "Volume unknown" warning badge. */
  volumeUnknown: boolean;
}

/** One label-above-value row of the debug-only "Context" block (redesign). */
interface ContextRow {
  label: string;
  value: string;
}

/**
 * Icon + text rollup of the execution outcome for `state === "executed"` (redesign row):
 * one 20px icon + full-text line; the raw undone task ids of a partial pass ride the tooltip.
 */
interface OutcomeRow {
  icon: string;
  stateClass: string;
  text: string;
  /** Raw undone task ids (partial pass) — empty string disables the tooltip. */
  tooltip: string;
}

/** Render model of the failure/diagnosis section (redesign). */
interface FailureSection {
  /** `failure.user_message` — null when no failure record accompanies the blocked plan. */
  message: string | null;
  /** Technical diagnosis: `failure.detail`, else the first run-engine `last_issue_code`. */
  diagnosis: string;
  /** Active feedback questions rendered as informational rows (never a form). */
  questions: FeedbackQuestion[];
}

/** First profile workarea of the approval surface (redesign; null hides the section). */
interface ApprovalWorkarea {
  /** `reason` of the workarea; falls back to the raw `workarea_id` when no reason is present. */
  text: string;
  /** "Used by N tasks" (N = `used_by_task_ids.length`). */
  usedByText: string;
}

/**
 * Maps a live `BlackboardStreamService` row state (`ActionState`, the SSE v2 transient
 * `"running"` on attempt-start — B1, or the collapsed `"WAITING"` of the status contract v3)
 * onto the shared `PlanTaskState` vocabulary: the transient running attempt reads as READY
 * (the task is being worked, not parked); everything else passes through verbatim.
 */
function liveTaskState(state: ActionState | "running" | "WAITING"): PlanTaskState {
  return state === "running" ? "READY" : state;
}

/**
 * Read-only card for the `plan` content block — the raw contract Plan aggregate (doc 31 §5),
 * redesigned 2026-10 to the plan-cards spec: a clamped 2-line objective header with the
 * icon-only lifecycle state and the revision/dry-run badges; an approval warnbox while
 * `pending_approval` (unresolved decision checklist with readable titles, defensive payload
 * readouts and the Mandatory/Optional chips, plus the first profile workarea); the steps as an
 * ALWAYS-visible shared grouped list (`PlanStepListComponent`) with per-task live engine
 * states joined from `BlackboardStreamService` by `task_id` (doc 31 §7.5) falling back to the
 * persisted `run_engine.task_executions` states; the execution outcome row once executed; the
 * failure/diagnosis section for blocked plans (`failed`/`pending_feedback`) with the user
 * message, the technical diagnosis and the active feedback questions as informational rows;
 * the debug-only "Context" block for the profile pieces; and a debug-only footer with the
 * Debug button opening `PlanDebugDialogComponent` (the raw plan dump lives only in that dialog).
 * There are NO approve/feedback form controls: approve/reject/
 * feedback/run happen through the conversation (backend tools) — the card only mirrors state
 * (doc 31 §10.1).
 */
@Component({
  selector: "app-plan-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCard, MatCardContent, MatButton, MatIcon, MatTooltip, PlanStepListComponent],
  templateUrl: "./plan-card.component.html",
  styleUrl: "./plan-card.component.scss",
})
export class PlanCardComponent {
  /** Raw contract Plan aggregate (doc 31 §5) carried by the `plan` content block. */
  readonly data = input.required<PlanData>();

  /**
   * Envelope status, kept for binding symmetry with the content dispatcher and the
   * workspace-index debug card — deliberately UNUSED: the fine lifecycle detail derives from
   * `data.state` (doc 31 §4.2), never from `value.status`.
   */
  readonly status = input.required<BlockStatus>();

  /** Live per-task engine rows (doc 31 §7.3), joined by task_id. Null outside a chat container. */
  readonly #blackboard = inject(BlackboardStreamService, { optional: true });

  /** Live frontend_tools interrupt — marks tasks the browser executes itself. */
  readonly #hitl = inject(HitlService, { optional: true });

  /**
   * Live evidence store (per-container, same lifecycle as the blackboard stream); null outside
   * a chat container — the debug dialog then renders its "Evidence events" empty state.
   */
  readonly #evidenceProgress = inject(EvidenceProgressStore, { optional: true });

  readonly #debugMode = inject(DebugModeService);

  /** Opens the plan debug dialog — see `openDebug`. */
  readonly #dialog = inject(MatDialog);

  /** Signal alias for the live task board (doc 31 §7.3 rows); null without a stream service. */
  private readonly taskRows = this.#blackboard?.taskRows ?? null;

  /** Debug toggle signal (Alt+0) — gates the footer Debug button only. */
  protected readonly isDebugMode = this.#debugMode.isDebugMode;

  /** True when this is a dry-run plan (badge only — no parent nesting, doc 31 §6). */
  protected readonly dryRun = computed(() => this.data().plan_type === "dry_run");

  /**
   * Icon-only lifecycle state for the header, mapped straight from `data.state` (redesign
   * 2026-10 state map): `pending_approval`/`pending_feedback` render `hourglass_top` in the
   * warn tone — both wait on the user; `approved`/`executed` render `task_alt` in the ok
   * tone; `failed`/`rejected` render `error_outline` in the err tone (the `pending_feedback`
   * and `rejected` mappings extend the spec's base trio so every lifecycle state stays
   * covered); the draft state `null` HIDES the icon entirely — the planning spinner belongs
   * to the live progress card, never to this header. The human state word lives in the icon
   * tooltip only (V2 ruling).
   */
  protected readonly headerState = computed<PlanStateIcon | null>(() => {
    switch (this.data().state) {
      case "pending_approval":
        return { icon: "hourglass_top", stateClass: "text-warning", word: "Pending approval" };
      case "pending_feedback":
        return { icon: "hourglass_top", stateClass: "text-warning", word: "Pending feedback" };
      case "approved":
        return { icon: "task_alt", stateClass: "text-success", word: "Approved" };
      case "executed":
        return { icon: "task_alt", stateClass: "text-success", word: "Executed" };
      case "failed":
        return { icon: "error_outline", stateClass: "mat-text-error", word: "Failed" };
      case "rejected":
        return { icon: "error_outline", stateClass: "mat-text-error", word: "Rejected" };
      default:
        return null;
    }
  });

  /** Unresolved decisions the user must take before approving (doc 31 §10.1) — informational. */
  protected readonly pendingDecisions = computed(
    () => this.data().phases.plan_assessment.review_outcome?.pending_decisions.filter((d) => !d.resolved) ?? [],
  );

  /**
   * Approval checklist rows (redesign): each unresolved decision paired with its readable
   * title, header icon and the DEFENSIVE payload readouts keyed by presence — the volume row
   * renders when the payload carries a numeric `volume`, the sample-size row when it carries
   * a numeric `sample_size`, and the "Volume unknown" badge when `volume_known === false`.
   */
  protected readonly pendingDecisionRows = computed<PendingDecisionRow[]>(() =>
    this.pendingDecisions().map((decision) => ({
      decision,
      title: decisionTitle(decision.decision_type),
      icon: decisionIcon(decision.decision_type),
      volume: payloadNumber(decision.payload, "volume"),
      sampleSize: payloadNumber(decision.payload, "sample_size"),
      volumeUnknown: payloadBoolean(decision.payload, "volume_known") === false,
    })),
  );

  /**
   * First profile workarea for the approval surface (redesign): the workarea REASON as the
   * row text (R4d) with the raw id fallback, and the used-by task count beside it. Only the
   * first workarea renders; null when the plan is not `pending_approval` or the profile
   * carries no workareas.
   */
  protected readonly approvalWorkarea = computed<ApprovalWorkarea | null>(() => {
    if (this.data().state !== "pending_approval") return null;
    const workarea = this.data().phases.plan_assessment.profile?.workareas[0] ?? null;
    if (!workarea) return null;
    const count = workarea.used_by_task_ids.length;
    return {
      text: workarea.reason || workarea.workarea_id,
      usedByText: `Used by ${count} ${count === 1 ? "task" : "tasks"}`,
    };
  });

  /**
   * True when the approval warnbox (redesign) has content to host: the box renders ONLY while
   * `pending_approval` AND carries at least one unresolved decision or a profile workarea, so
   * an approval state without data renders no empty warning container.
   */
  protected readonly showApprovalBox = computed(
    () =>
      this.data().state === "pending_approval" &&
      (this.pendingDecisionRows().length > 0 || this.approvalWorkarea() !== null),
  );

  /**
   * "Context" rows (redesign: DEBUG-ONLY, label-above-value): Batch = the batch
   * `entity_type` plus its source task/evidence ref when present, Target = entity type ·
   * cardinality, Workarea = the first profile workarea id. Missing profile pieces fall back
   * to "—" so the block renders even before the assessment phase populated the profile
   * (plan-cards-spec §Plan card).
   */
  protected readonly contextRows = computed<ContextRow[]>(() => {
    const profile = this.data().phases.plan_assessment.profile;
    const batch = profile?.batch ?? null;
    let batchValue = "—";
    if (batch) {
      const source = batch.source.task_id
        ? `task ${batch.source.task_id}`
        : batch.source.evidence_id
        ? `evidence ${batch.source.evidence_id}`
        : null;
      batchValue = source ? `${batch.entity_type} — from ${source}` : batch.entity_type;
    }
    return [
      { label: "Batch", value: batchValue },
      {
        label: "Target",
        value: profile ? `${profile.target_entity.entity_type} · ${profile.target_entity.cardinality}` : "—",
      },
      { label: "Workarea", value: profile?.workareas[0]?.workarea_id ?? "—" },
    ];
  });

  /** Task intent per `task_id` — join anchor for the partial-outcome rollup (R4d). */
  protected readonly taskIntentById = computed(() => {
    const intents = new Map<string, string>();
    for (const step of this.data().steps) {
      for (const task of step.tasks) intents.set(task.task_id, task.intent);
    }
    return intents;
  });

  /**
   * Steps for the shared grouped list (redesign: always visible, no accordion): text, the
   * existing `temporal_constraint · order:` meta line, the step confidence, and one task per
   * intent whose state joins the LIVE `BlackboardStreamService` row by `task_id` (doc 31
   * §7.5 — the SSE v2 transient `running` reads as READY) falling back to the persisted
   * `run_engine.task_executions` state, with the attempts count from the same source.
   */
  protected readonly stepListSteps = computed<PlanStepListStep[]>(() => {
    const liveRows = this.taskRows?.() ?? null;
    const liveFrontendIds = new Set(frontendToolTaskIds(this.#hitl?.frontendToolsPayload()));
    const persisted = new Map(
      this.data().phases.run_engine.task_executions.map((execution) => [execution.task_id, execution]),
    );
    return this.data().steps.map((step) => ({
      text: step.text,
      meta:
        step.order_hint.length > 0
          ? `${step.temporal_constraint} · order: ${step.order_hint.join(", ")}`
          : step.temporal_constraint,
      confidence: step.confidence,
      tasks: step.tasks.map((task) => {
        const row = liveRows?.get(task.task_id) ?? null;
        const execution = persisted.get(task.task_id) ?? null;
        return {
          text: task.intent,
          state: row ? liveTaskState(row.state) : execution?.state ?? "PENDING",
          attempts: row ? row.attempts : execution?.attempts.length ?? 0,
          frontendTool:
            liveFrontendIds.has(task.task_id) ||
            frontendToolTaskIds(execution?.diagnostics?.pending_reason?.interrupt_payload).includes(task.task_id),
        };
      }),
    }));
  });

  /**
   * Outcome rollup for `state === "executed"` (doc 31 §10.1, redesign row): `all_produced`
   * renders the completed copy in the ok tone; `partial`/`none_produced` keep the same row
   * layout in the err tone (results are missing). R4d: the partial pass joins the undone
   * tasks' INTENTS on the surface — raw ids ride the tooltip (the plan join can miss, so an
   * unknown id falls back to itself).
   */
  protected readonly outcomeRow = computed<OutcomeRow | null>(() => {
    if (this.data().state !== "executed") return null;
    const execution = this.data().phases.run_engine.execution;
    if (!execution) return null;
    switch (execution.outcome) {
      case "all_produced":
        return {
          icon: "check_circle",
          stateClass: "text-success",
          text: "Completed: every task produced results.",
          tooltip: "",
        };
      case "partial": {
        const undone = nonDoneTaskIds(this.data().phases.run_engine.task_executions);
        const text =
          undone.length === 0
            ? "Partially completed."
            : `Partially completed — tasks not done: ${undone
                .map((id) => this.taskIntentById().get(id) ?? id)
                .join(", ")}`;
        return { icon: "error_outline", stateClass: "mat-text-error", text, tooltip: undone.join(", ") };
      }
      case "none_produced":
        return {
          icon: "error_outline",
          stateClass: "mat-text-error",
          text: "Run finished without results.",
          tooltip: "",
        };
    }
  });

  /**
   * Failure/diagnosis section (redesign): rendered for the BLOCKED plan states — `failed` and
   * `pending_feedback`. The task brief names `failed`, but `PlanFailure` is the diagnosis of
   * BOTH blocked states (doc 31 §5.4) and the feedback questions only travel with
   * `pending_feedback`, so both share the section; null when neither carries content. The
   * diagnosis value reads `failure.detail` first and falls back to the first run-engine
   * `last_issue_code`.
   */
  protected readonly failureSection = computed<FailureSection | null>(() => {
    const state = this.data().state;
    if (state !== "failed" && state !== "pending_feedback") return null;
    const failure = this.data().failure;
    const questions = this.data().feedback_request?.questions ?? [];
    if (!failure && questions.length === 0) return null;
    const lastIssueCode =
      this.data()
        .phases.run_engine.task_executions.map((execution) => execution.diagnostics.last_issue_code)
        .find((code) => code !== null) ?? null;
    return {
      message: failure?.user_message ?? null,
      diagnosis: failure?.detail || lastIssueCode || "—",
      questions,
    };
  });

  /**
   * Opens the plan debug dialog (redesign): the former inline `<details>` debug panel is
   * replaced by the shared dialog — the footer button is gated on `DebugModeService`.
   */
  protected openDebug(): void {
    PlanDebugDialogComponent.open(this.#dialog, this.data(), this.#evidenceProgress);
  }
}
